'use client'

import React, { useRef, useState } from 'react'

import { Box, Flex } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Tooltip } from '../Tooltip/Tooltip'
import { countStyles, toggleStyles } from './CountToggle.styles'

export type CountToggleProps = {
  /** Symbol rendered on the left. Renders with `currentColor`, which the
   * component sets from the active `colorScheme`. */
  icon: React.ReactNode
  /** Numeric badge shown next to the icon when `hasCount` is true. */
  count?: number
  /** Whether to render the count badge. Defaults to true. */
  hasCount?: boolean
  /** Hover tooltip label. When omitted no tooltip is shown. */
  tooltip?: string
  /** Active/selected visual state. */
  isActive?: boolean
  onClick?: () => void
  colorScheme?: ColorScheme
  dataTestId?: string
}

/**
 * A compact header/toolbar control: an icon with an optional count badge, an
 * active state, and a hover tooltip. Generic — the consumer supplies the icon
 * and decides what `onClick` does. Must be rendered inside a `DarkProvider`.
 */
export const CountToggle = ({
  icon,
  count,
  hasCount = true,
  tooltip,
  isActive = false,
  onClick,
  colorScheme = 'dark',
  dataTestId,
}: CountToggleProps) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [isHovering, setIsHovering] = useState(false)

  return (
    <>
      <Flex
        {...toggleStyles(isActive, colorScheme)}
        data-testid={dataTestId}
        onClick={onClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        ref={anchorRef}
      >
        {icon}
        {hasCount && <Box {...countStyles}>{count ? count : '-'}</Box>}
      </Flex>
      {tooltip && isHovering && (
        <Tooltip
          anchorEl={anchorRef.current}
          colorScheme={colorScheme}
          label={tooltip}
          placement='bottom'
        />
      )}
    </>
  )
}
